import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { Modal, Radio, Space, Typography, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

import { useBuildingContext } from './BuildingContext';

type PrintAction = (buildingId: string) => Promise<void> | void;

interface PromptOptions {
	title?: string;
	description?: ReactNode;
	okText?: string;
}

interface PrintLocationContextValue {
	promptForBuilding: (action: PrintAction, options?: PromptOptions) => void;
}

const PrintLocationContext = createContext<PrintLocationContextValue | null>(null);

const DEFAULT_TITLE = 'Where do you want to print?';
const DEFAULT_OK_TEXT = 'Print';

export function PrintLocationProvider({ children }: { children: ReactNode }) {
	const { buildingId, buildings } = useBuildingContext();
	const [open, setOpen] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [pendingAction, setPendingAction] = useState<PrintAction | null>(null);
	const [options, setOptions] = useState<PromptOptions>({});
	const [submitting, setSubmitting] = useState(false);

	const promptForBuilding = useCallback<PrintLocationContextValue['promptForBuilding']>(
		(action, opts) => {
			if (buildings.length === 0) {
				void action('');
				return;
			}
			if (buildings.length === 1) {
				void action(buildings[0].id);
				return;
			}
			setOptions(opts ?? {});
			setSelectedId(buildingId ?? buildings[0].id);
			setPendingAction(() => action);
			setOpen(true);
		},
		[buildings, buildingId],
	);

	const handleCancel = useCallback(() => {
		if (submitting) return;
		setOpen(false);
		setPendingAction(null);
	}, [submitting]);

	const handleConfirm = useCallback(async () => {
		if (!pendingAction || !selectedId) return;
		setSubmitting(true);
		try {
			await pendingAction(selectedId);
		} finally {
			setSubmitting(false);
			setOpen(false);
			setPendingAction(null);
		}
	}, [pendingAction, selectedId]);

	const value = useMemo<PrintLocationContextValue>(
		() => ({ promptForBuilding }),
		[promptForBuilding],
	);

	return (
		<PrintLocationContext.Provider value={value}>
			{children}
			<Modal
				open={open}
				title={
					<Space>
						<PrinterOutlined />
						<span>{options.title ?? DEFAULT_TITLE}</span>
					</Space>
				}
				onCancel={handleCancel}
				centered
				width={460}
				zIndex={2000}
				maskClosable={!submitting}
				closable={!submitting}
				footer={[
					<Button key="cancel" onClick={handleCancel} disabled={submitting}>
						Cancel
					</Button>,
					<Button
						key="confirm"
						type="primary"
						onClick={handleConfirm}
						loading={submitting}
						disabled={!selectedId}
					>
						{options.okText ?? DEFAULT_OK_TEXT}
					</Button>,
				]}
			>
				{options.description && (
					<Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
						{options.description}
					</Typography.Paragraph>
				)}
				<Radio.Group
					value={selectedId}
					onChange={(e) => setSelectedId(e.target.value)}
					style={{ width: '100%' }}
				>
					<Space direction="vertical" size={12} style={{ width: '100%' }}>
						{buildings.map((b) => (
							<Radio key={b.id} value={b.id} style={{ fontSize: 16 }}>
								{b.name}
							</Radio>
						))}
					</Space>
				</Radio.Group>
			</Modal>
		</PrintLocationContext.Provider>
	);
}

export function usePrintLocation(): PrintLocationContextValue {
	const ctx = useContext(PrintLocationContext);
	if (!ctx) {
		throw new Error('usePrintLocation must be used within a PrintLocationProvider');
	}
	return ctx;
}
